// @flow
import React, { useMemo, useState } from 'react';
import ConfirmDialog from "../../../../../components/app/dialogs/confirmDialog/confirmDialog";
import { useCategories } from "../../../../../hooks/categories/useCategories";
import { useRules } from "../../../../../hooks/rules/useRules";
import EditableMUIDataGrid from "../../../../../components/grids/editableMUIDataGrid/editableMUIDataGrid";
import { useTranslation } from "react-i18next";
import { RULES } from "../../../../../locales/components/namespaces";
import Box from "@mui/material/Box";
import { useRulesAction } from "../../../../../hooks/rules/useRulesAction";
import { useRulesData } from "../../../../../hooks/rules/useRulesData";
import { Alert, Snackbar } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { Tune } from "@mui/icons-material";
import RuleConditions from "./partials/ruleConditions";
import { useCountries } from "../../../../../hooks/countries/useCountries";
import { useInterests } from "../../../../../hooks/interests/useInterests";

type Props = {
	exhibitionId: string
};

const Rules = (props: Props) => {
	const { exhibitionId } = props
	const { rules, loading } = useRules(exhibitionId)
	const { loading: loadingCategories, categories } = useCategories(exhibitionId)
	const { loading: loadingCountries, countries } = useCountries()
	const { loading: loadingInterests, interests } = useInterests(exhibitionId)
	const { saveRule, updateRule, deleteRule, clearRuleStatus } = useRulesAction({ exhibitionId })
	const { status } = useRulesData()
	const { t } = useTranslation(RULES)
	const [openRuleConditions, setOpenRuleConditions] = useState(null)
	
	const newRowModel = {
		description: '',
		characterizationId: ''
	}
	
	const columns = useMemo(() => [
		{
			field: 'ruleConditions',
			headerName: t('Rule Conditions'),
			type: 'actions',
			width: 140,
			getActions: (params) => [
				<GridActionsCellItem
					onClick={() => setOpenRuleConditions(params.row.id)}
					icon={<Tune/>}
					label={t("Open Rule Conditions")}
				/>,
			],
		},
		{
			field: 'description',
			headerName: t('Description'),
			flex: 1,
			editable: true
		},
		{
			field: 'characterizationId',
			headerName: t('Characterization'),
			flex: 1,
			type: 'singleSelect',
			valueOptions: categories.map(category => ({
				value: category.id,
				label: category.text
			})),
			editable: true
		},
	], [categories, t])
	
	return (
		<Box>
			<EditableMUIDataGrid
				data={rules}
				columns={columns}
				loading={loading}
				newRowModel={newRowModel}
				store={saveRule}
				remove={deleteRule}
				update={updateRule}
			/>
			<Snackbar
				open={Boolean(status)}
				anchorOrigin={{
					vertical: 'top', horizontal: 'right',
				}}
				autoHideDuration={4000}
				onClose={clearRuleStatus}>
				<Alert
					onClose={clearRuleStatus}
					severity={status?.type}
					sx={{ width: '100%', fontSize: '16px' }}
				>
					{t(status?.message)}
				</Alert>
			</Snackbar>
			<ConfirmDialog
				maxWidth={'lg'}
				open={Boolean(openRuleConditions)}
				handleDialog={() => {
					setOpenRuleConditions(null)
				}}
				title={t('Add Rule Conditions')}
				otherDialogProps={{ keepMounted: true, disableEscapeKeyDown: true, fullWidth: '100%' }}
				onNo={() => {
					setOpenRuleConditions(null)
				}}
				yesAction={false}
				noAction={false}
			>
				<RuleConditions
					ruleId={openRuleConditions}
					exhibitionId={exhibitionId}
					countries={countries}
					interests={interests}
				/>
			</ConfirmDialog>
		</Box>
	);
};

export default Rules